// Assignment Engine API — orchestrates period discovery, eligibility filtering,
// priority sorting, and draft assignment creation.

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import { getSubstitutionRules } from './rules-api'
import { writeAuditEntry } from './substitution-audit-api'
import { discoverPeriods } from './engine/period-discovery'
import { filterEligibleCandidates } from './engine/eligibility'
import { sortCandidates, buildMatchReasons } from './engine/priority'
import type {
  EngineResult,
  BulkEngineResult,
  DraftAssignment,
  Teacher,
  SubstitutionRules,
  AutoAssignProgressCallback,
  PeriodProgressState,
  AvailabilityBreakdown,
} from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

// ============================================================================
// Helpers
// ============================================================================

function authHeaders(accessToken: string): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

/** Returns Monday (ISO week start) for a given ISO date string */
function getWeekBounds(isoDate: string): { weekStart: string; weekEnd: string } {
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  const dow = d.getDay()
  const diffToMonday = dow === 0 ? -6 : 1 - dow
  const monday = new Date(d)
  monday.setDate(d.getDate() + diffToMonday)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const fmt = (dt: Date) => dt.toISOString().split('T')[0]
  return { weekStart: fmt(monday), weekEnd: fmt(sunday) }
}

const DAY_OF_WEEK_MAP: Record<number, string> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
}

function getDayOfWeek(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return DAY_OF_WEEK_MAP[d.getDay()]
}

/** Add days to an ISO date string */
function addDays(isoDate: string, n: number): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  // Use UTC to avoid timezone issues
  const d = new Date(Date.UTC(year, month - 1, day))
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().split('T')[0]
}

/** Get today's ISO date string */
function todayISO(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Fetch all active teachers for a school with their subjects and class associations.
 */
async function fetchTeachersWithRelations(
  schoolId: string,
  accessToken: string
): Promise<{
  teachers: Teacher[]
  teacherSubjects: Map<string, Set<string>>
  teacherClassAssociations: Map<string, Set<string>>
  teacherClassGroupAssociations: Map<string, Set<string>>
}> {
  const headers = authHeaders(accessToken)

  // Fetch all active teachers
  const teacherParams = new URLSearchParams()
  teacherParams.append('select', '*')
  teacherParams.append('school_id', `eq.${schoolId}`)
  teacherParams.append('is_active', `eq.true`)

  const teacherResponse = await fetch(`${SUPABASE_URL}/rest/v1/teachers?${teacherParams}`, {
    headers,
  })

  if (!teacherResponse.ok) {
    const err = await teacherResponse.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to fetch teachers')
  }

  const teachers: Teacher[] = await teacherResponse.json()

  // Fetch teacher subjects
  const subjectParams = new URLSearchParams()
  subjectParams.append('select', 'teacher_id,subject_id')
  subjectParams.append('school_id', `eq.${schoolId}`)

  const subjectResponse = await fetch(`${SUPABASE_URL}/rest/v1/teacher_subjects?${subjectParams}`, {
    headers,
  })

  const teacherSubjects = new Map<string, Set<string>>()
  if (subjectResponse.ok) {
    const rows: { teacher_id: string; subject_id: string }[] = await subjectResponse.json()
    for (const row of rows) {
      if (!teacherSubjects.has(row.teacher_id)) teacherSubjects.set(row.teacher_id, new Set())
      teacherSubjects.get(row.teacher_id)!.add(row.subject_id)
    }
  }

  // Fetch teacher class associations (from timetable_records — teachers who teach a class section)
  // We use the published academic year's timetable records to derive class associations
  const ayParams = new URLSearchParams()
  ayParams.append('select', 'id')
  ayParams.append('school_id', `eq.${schoolId}`)
  ayParams.append('status', `eq.published`)

  const ayResponse = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${ayParams}`, { headers })
  const publishedYearIds: string[] = []
  if (ayResponse.ok) {
    const rows: { id: string }[] = await ayResponse.json()
    for (const row of rows) publishedYearIds.push(row.id)
  }

  const teacherClassAssociations = new Map<string, Set<string>>()
  const teacherClassGroupAssociations = new Map<string, Set<string>>()

  if (publishedYearIds.length > 0) {
    const assocParams = new URLSearchParams()
    assocParams.append(
      'select',
      'teacher_id,class_section_id,class_section:class_sections(class_id,class:classes(class_group_id))'
    )
    assocParams.append('academic_year_id', `in.(${publishedYearIds.join(',')})`)

    const assocResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?${assocParams}`,
      { headers }
    )

    if (assocResponse.ok) {
      const rows: any[] = await assocResponse.json()
      for (const row of rows) {
        const tid = row.teacher_id
        const csid = row.class_section_id
        const cgid = row.class_section?.class?.class_group_id

        if (!teacherClassAssociations.has(tid)) teacherClassAssociations.set(tid, new Set())
        teacherClassAssociations.get(tid)!.add(csid)

        if (cgid) {
          if (!teacherClassGroupAssociations.has(tid))
            teacherClassGroupAssociations.set(tid, new Set())
          teacherClassGroupAssociations.get(tid)!.add(cgid)
        }
      }
    }
  }

  return { teachers, teacherSubjects, teacherClassAssociations, teacherClassGroupAssociations }
}

/**
 * Fetch posted assignment counts per teacher for fairness sorting.
 * Returns daily and weekly counts using only posted assignments.
 */
async function fetchPostedAssignmentCounts(
  targetDate: string,
  accessToken: string
): Promise<Map<string, { daily: number; weekly: number }>> {
  const headers = authHeaders(accessToken)
  const { weekStart, weekEnd } = getWeekBounds(targetDate)

  const params = new URLSearchParams()
  params.append('select', 'substitute_teacher_id,date')
  params.append('date', `gte.${weekStart}`)
  params.append('date', `lte.${weekEnd}`)
  params.append('status', `eq.posted`)
  params.append('substitute_teacher_id', `not.is.null`)

  const response = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${params}`, { headers })

  const counts = new Map<string, { daily: number; weekly: number }>()
  if (response.ok) {
    const rows: { substitute_teacher_id: string; date: string }[] = await response.json()
    for (const row of rows) {
      const tid = row.substitute_teacher_id
      if (!counts.has(tid)) counts.set(tid, { daily: 0, weekly: 0 })
      const c = counts.get(tid)!
      c.weekly++
      if (row.date === targetDate) c.daily++
    }
  }

  return counts
}

// ============================================================================
// Progress helpers
// ============================================================================

/** Format a time string like "09:00:00" → "9:00 AM" */
function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

/** Build a time range string from two HH:MM:SS strings */
function timeRange(start: string, end: string): string {
  return `${formatTime(start)} – ${formatTime(end)}`
}

// ============================================================================
// triggerEngine
// ============================================================================

/**
 * Run the assignment engine for a single absence on a specific target date.
 *
 * Flow:
 * 1. Load absence + validate it's not cancelled
 * 2. Check for existing draft/posted plan (skip if exists)
 * 3. Load substitution rules
 * 4. Discover periods for the absent teacher on targetDate
 * 5. For each period: filter eligible candidates → sort → pick top → build assignment
 * 6. Save all assignments as draft in a single batch
 * 7. Write audit log entry
 *
 * @param onProgress Optional callback — called at each engine checkpoint with real data.
 *   The UI uses this to drive the progress animation. No dummy data is ever emitted.
 */
export async function triggerEngine(
  absenceId: string,
  targetDate: string,
  onProgress?: AutoAssignProgressCallback,
  /** Shared across a bulk run — prevents double-booking the same teacher at the same period */
  sharedDraftBatchAssignedPeriods?: Map<string, Array<{ time_start: string; time_end: string }>>
): Promise<EngineResult> {
  return handleApiCall(async () => {
    const startTime = Date.now()
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Step 1: Load absence (with teacher join for name resolution)
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', '*,teacher:teachers(id,name)')
    absenceParams.append('id', `eq.${absenceId}`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      const msg = err.message || 'Failed to fetch absence'
      onProgress?.({
        phase: 'error',
        message: msg,
        absentTeacherName: '',
        periodsFound: 0,
        periodStates: [],
        periodsAssigned: 0,
        periodsUnassigned: 0,
        errorMessage: msg,
      })
      throw new Error(msg)
    }

    const absences: any[] = await absenceResponse.json()
    if (!absences || absences.length === 0) {
      const msg = 'Absence not found'
      onProgress?.({
        phase: 'error',
        message: msg,
        absentTeacherName: '',
        periodsFound: 0,
        periodStates: [],
        periodsAssigned: 0,
        periodsUnassigned: 0,
        errorMessage: msg,
      })
      throw new Error(msg)
    }

    const absence = absences[0]
    // Teacher name is not on the raw absence row — we resolve it from the joined
    // teacher record if available, otherwise fall back to a placeholder.
    // The absences table only stores teacher_id; the name comes from a join.
    const absentTeacherName: string =
      (absence as any).teacher?.name ?? (absence as any).teacher_name ?? ''

    // Check if cancelled
    if (absence.status === 'cancelled') {
      onProgress?.({
        phase: 'skipped',
        message: 'This absence has been cancelled — no substitutes needed.',
        absentTeacherName,
        periodsFound: 0,
        periodStates: [],
        periodsAssigned: 0,
        periodsUnassigned: 0,
        skippedReason: 'cancelled',
        elapsedMs: Date.now() - startTime,
      })
      return {
        target_date: targetDate,
        absence_id: absenceId,
        periods_affected: 0,
        periods_assigned: 0,
        periods_unassigned: 0,
        assignments: [],
        skipped_reason: 'cancelled',
      }
    }

    // Step 2: Check for existing draft/posted plan
    const existingParams = new URLSearchParams()
    existingParams.append('select', 'id')
    existingParams.append('absence_id', `eq.${absenceId}`)
    existingParams.append('date', `eq.${targetDate}`)
    existingParams.append('status', `in.(draft,posted)`)
    existingParams.append('limit', '1')

    const existingResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${existingParams}`,
      { headers }
    )

    if (existingResponse.ok) {
      const existing: any[] = await existingResponse.json()
      if (existing.length > 0) {
        onProgress?.({
          phase: 'skipped',
          message: 'A plan already exists for this date — use Discard to start fresh.',
          absentTeacherName,
          periodsFound: 0,
          periodStates: [],
          periodsAssigned: 0,
          periodsUnassigned: 0,
          skippedReason: 'draft_exists',
          elapsedMs: Date.now() - startTime,
        })
        return {
          target_date: targetDate,
          absence_id: absenceId,
          periods_affected: 0,
          periods_assigned: 0,
          periods_unassigned: 0,
          assignments: [],
          skipped_reason: 'draft_exists',
        }
      }
    }

    // Step 3: Load substitution rules
    const rules: SubstitutionRules = await getSubstitutionRules(absence.school_id)

    // Phase 1 — emit discovery start
    onProgress?.({
      phase: 'discovery',
      message: `Checking what classes ${absentTeacherName || 'this teacher'} was supposed to teach…`,
      absentTeacherName,
      periodsFound: 0,
      periodStates: [],
      periodsAssigned: 0,
      periodsUnassigned: 0,
    })

    // Step 4: Discover periods
    const discoveryResult = await discoverPeriods(
      absence.teacher_id,
      targetDate,
      absence.absence_type,
      absence.start_time,
      absence.end_time,
      absence.school_id
    )

    if (discoveryResult.skipped_reason) {
      const skipMessages: Record<string, string> = {
        no_published_academic_year: 'No published academic year found — timetable is not active.',
        non_working_day: 'This date is a holiday or non-working day — no substitutes needed.',
        no_scheduled_periods: `${absentTeacherName || 'This teacher'} has no scheduled classes on this day.`,
      }
      const msg = skipMessages[discoveryResult.skipped_reason] ?? 'No periods to cover.'
      onProgress?.({
        phase: 'skipped',
        message: msg,
        absentTeacherName,
        periodsFound: 0,
        periodStates: [],
        periodsAssigned: 0,
        periodsUnassigned: 0,
        skippedReason: discoveryResult.skipped_reason,
        elapsedMs: Date.now() - startTime,
      })
      return {
        target_date: targetDate,
        absence_id: absenceId,
        periods_affected: 0,
        periods_assigned: 0,
        periods_unassigned: 0,
        assignments: [],
        skipped_reason: discoveryResult.skipped_reason,
      }
    }

    const { periods } = discoveryResult

    if (periods.length === 0) {
      onProgress?.({
        phase: 'skipped',
        message: `${absentTeacherName || 'This teacher'} has no scheduled classes on this day.`,
        absentTeacherName,
        periodsFound: 0,
        periodStates: [],
        periodsAssigned: 0,
        periodsUnassigned: 0,
        skippedReason: 'no_scheduled_periods',
        elapsedMs: Date.now() - startTime,
      })
      return {
        target_date: targetDate,
        absence_id: absenceId,
        periods_affected: 0,
        periods_assigned: 0,
        periods_unassigned: 0,
        assignments: [],
        skipped_reason: 'no_scheduled_periods',
      }
    }

    // Compute time range across all periods
    const allStarts = periods.map((p) => p.period.time_start).sort()
    const allEnds = periods.map((p) => p.period.time_end).sort()
    const periodTimeRange = timeRange(allStarts[0], allEnds[allEnds.length - 1])

    // Emit Phase 1 complete
    onProgress?.({
      phase: 'discovery',
      message: `Found ${periods.length} period${periods.length !== 1 ? 's' : ''} that need substitute teachers`,
      absentTeacherName,
      periodsFound: periods.length,
      periodTimeRange,
      periodStates: [],
      periodsAssigned: 0,
      periodsUnassigned: 0,
    })

    // Step 5: Fetch all teachers with relations for this school
    // Phase 2 — emit availability check start
    onProgress?.({
      phase: 'availability',
      message: 'Looking for available teachers…',
      absentTeacherName,
      periodsFound: periods.length,
      periodTimeRange,
      periodStates: [],
      periodsAssigned: 0,
      periodsUnassigned: 0,
    })

    const { teachers, teacherSubjects, teacherClassAssociations, teacherClassGroupAssociations } =
      await fetchTeachersWithRelations(absence.school_id, accessToken)

    // Fetch posted assignment counts for fairness sorting
    const postedAssignmentsByTeacher = await fetchPostedAssignmentCounts(targetDate, accessToken)

    const dayOfWeek = getDayOfWeek(targetDate)

    // Track draft batch counts (updated as we assign each period)
    const draftBatchCounts = new Map<string, { daily: number; weekly: number }>()

    // Track teachers assigned in this run (for consecutive avoidance)
    const currentRunAssignedTeacherIds = new Set<string>()

    // Track which time slots each teacher has been assigned to in this batch.
    // Uses the shared map when called from a bulk run so cross-absence conflicts
    // are detected; falls back to a local map for single-absence runs.
    const draftBatchAssignedPeriods =
      sharedDraftBatchAssignedPeriods ?? new Map<string, Array<{ time_start: string; time_end: string }>>()

    // Run eligibility on the first period to get the availability breakdown
    // (subsequent periods may differ slightly due to quota changes, but the
    // school-wide absent/unavailable/busy counts are stable across periods)
    const firstPeriodEligibility = await filterEligibleCandidates(teachers, {
      targetDate,
      periodId: periods[0].period.id,
      periodTimeStart: periods[0].period.time_start,
      periodTimeEnd: periods[0].period.time_end,
      dayOfWeek,
      schoolId: absence.school_id,
      absentTeacherId: absence.teacher_id,
      rules,
      draftBatchCounts: new Map(), // use fresh map for the breakdown snapshot
      // draftBatchAssignedPeriods intentionally omitted — snapshot only
    })

    const availabilityBreakdown: AvailabilityBreakdown = firstPeriodEligibility.breakdown

    // Emit Phase 2 complete with real availability data
    onProgress?.({
      phase: 'availability',
      message: `${availabilityBreakdown.availableCount} teacher${availabilityBreakdown.availableCount !== 1 ? 's' : ''} available for substitution`,
      absentTeacherName,
      periodsFound: periods.length,
      periodTimeRange,
      availability: availabilityBreakdown,
      periodStates: [],
      periodsAssigned: 0,
      periodsUnassigned: 0,
    })

    // Phase 3 — matching
    // Initialise period state cards (all pending)
    const periodStates: PeriodProgressState[] = periods.map((p) => ({
      periodId: p.period.id,
      periodNumber: p.period.period_number,
      timeStart: p.period.time_start,
      timeEnd: p.period.time_end,
      subjectName: p.subject?.subject_name ?? '',
      className: `${p.class_section?.class?.class_name ?? ''}${p.class_section?.section ?? ''}`,
      assignedTeacher: null,
      matchReasons: [],
      topCandidates: [],
      status: 'pending',
    }))

    onProgress?.({
      phase: 'matching',
      message: 'Finding the best teacher for each period…',
      absentTeacherName,
      periodsFound: periods.length,
      periodTimeRange,
      availability: availabilityBreakdown,
      periodStates: [...periodStates],
      periodsAssigned: 0,
      periodsUnassigned: 0,
    })

    // Step 6: For each period, find the best candidate
    const assignmentsToInsert: any[] = []
    const resultAssignments: DraftAssignment[] = []

    for (let i = 0; i < periods.length; i++) {
      const periodCtx = periods[i]

      // Mark this period as searching
      periodStates[i] = { ...periodStates[i], status: 'searching' }
      onProgress?.({
        phase: 'matching',
        message: `Matching Period ${periodCtx.period.period_number} — ${periodCtx.subject?.subject_name ?? ''} (${periodCtx.class_section?.class?.class_name ?? ''}${periodCtx.class_section?.section ?? ''})`,
        absentTeacherName,
        periodsFound: periods.length,
        periodTimeRange,
        availability: availabilityBreakdown,
        periodStates: [...periodStates],
        periodsAssigned: periodStates.filter((s) => s.status === 'assigned').length,
        periodsUnassigned: periodStates.filter((s) => s.status === 'unassigned').length,
      })

      const eligibilityResult = await filterEligibleCandidates(teachers, {
        targetDate,
        periodId: periodCtx.period.id,
        periodTimeStart: periodCtx.period.time_start,
        periodTimeEnd: periodCtx.period.time_end,
        dayOfWeek,
        schoolId: absence.school_id,
        absentTeacherId: absence.teacher_id,
        rules,
        draftBatchCounts,
        draftBatchAssignedPeriods,
      })

      const eligibleCandidates = eligibilityResult.candidates

      let substituteTeacherId: string | null = null
      let unassignedReason: string | null = null
      const assignmentMethod: 'automatic' | 'manual' = 'automatic'

      if (eligibleCandidates.length === 0) {
        unassignedReason = 'no_eligible_teachers'
        periodStates[i] = {
          ...periodStates[i],
          status: 'unassigned',
          assignedTeacher: 'none',
          matchReasons: ['No eligible teachers available for this period'],
          topCandidates: [],
        }
      } else {
        // Sort candidates by priority
        const { sorted, topBreakdowns } = sortCandidates(eligibleCandidates, {
          period: periodCtx,
          rules,
          postedAssignmentsByTeacher,
          currentRunAssignedTeacherIds,
          teacherSubjects,
          teacherClassAssociations,
          teacherClassGroupAssociations,
        })

        if (rules.assignment_mode === 'auto') {
          // Pick the top candidate
          const topCandidate = sorted[0]
          substituteTeacherId = topCandidate.id

          // Update draft batch counts
          if (!draftBatchCounts.has(topCandidate.id)) {
            draftBatchCounts.set(topCandidate.id, { daily: 0, weekly: 0 })
          }
          const counts = draftBatchCounts.get(topCandidate.id)!
          counts.daily++
          counts.weekly++

          currentRunAssignedTeacherIds.add(topCandidate.id)

          // Record this time slot as taken by this teacher in the batch map
          // so cross-absence runs don't double-book the same slot
          if (!draftBatchAssignedPeriods.has(topCandidate.id)) {
            draftBatchAssignedPeriods.set(topCandidate.id, [])
          }
          draftBatchAssignedPeriods.get(topCandidate.id)!.push({
            time_start: periodCtx.period.time_start,
            time_end: periodCtx.period.time_end,
          })

          // Build match reasons and top candidates for animation
          const matchReasons = buildMatchReasons(topBreakdowns[0])
          const topCandidates = topBreakdowns.map((bd, idx) => ({
            name: bd.teacher.name,
            rank: (idx + 1) as 1 | 2 | 3,
            reason: buildMatchReasons(bd).join(', '),
          }))

          periodStates[i] = {
            ...periodStates[i],
            status: 'assigned',
            assignedTeacher: { id: topCandidate.id, name: topCandidate.name },
            matchReasons,
            topCandidates,
          }
        } else {
          // suggest mode — no auto-pick, but show top candidates
          const topCandidates = topBreakdowns.map((bd, idx) => ({
            name: bd.teacher.name,
            rank: (idx + 1) as 1 | 2 | 3,
            reason: buildMatchReasons(bd).join(', '),
          }))
          periodStates[i] = {
            ...periodStates[i],
            status: 'unassigned',
            assignedTeacher: null,
            matchReasons: ['Suggest mode — admin confirmation required'],
            topCandidates,
          }
        }
      }

      // Emit per-period update
      onProgress?.({
        phase: 'matching',
        message: substituteTeacherId
          ? `Period ${periodCtx.period.period_number}: ${periodStates[i].assignedTeacher !== null && periodStates[i].assignedTeacher !== 'none' ? (periodStates[i].assignedTeacher as { name: string }).name : ''} assigned`
          : `Period ${periodCtx.period.period_number}: No eligible teacher found`,
        absentTeacherName,
        periodsFound: periods.length,
        periodTimeRange,
        availability: availabilityBreakdown,
        periodStates: [...periodStates],
        periodsAssigned: periodStates.filter((s) => s.status === 'assigned').length,
        periodsUnassigned: periodStates.filter((s) => s.status === 'unassigned').length,
      })

      const assignmentRecord = {
        school_id: absence.school_id,
        absence_id: absenceId,
        date: targetDate,
        class_section_id: periodCtx.class_section_id,
        period_id: periodCtx.period.id,
        subject_id: periodCtx.subject_id,
        absent_teacher_id: absence.teacher_id,
        substitute_teacher_id: substituteTeacherId,
        status: 'draft',
        assignment_method: substituteTeacherId ? assignmentMethod : 'automatic',
        unassigned_reason: unassignedReason,
      }

      assignmentsToInsert.push(assignmentRecord)
    }

    // Step 7: Insert all assignments in a single batch (upsert to handle the unique constraint)
    if (assignmentsToInsert.length > 0) {
      const insertResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments`, {
        method: 'POST',
        headers: {
          ...headers,
          Prefer: 'return=representation,resolution=ignore-duplicates',
        },
        body: JSON.stringify(assignmentsToInsert),
      })

      if (!insertResponse.ok) {
        const err = await insertResponse.json().catch(() => ({}))
        const msg = err.message || 'Failed to insert draft assignments'
        onProgress?.({
          phase: 'error',
          message: msg,
          absentTeacherName,
          periodsFound: periods.length,
          periodTimeRange,
          availability: availabilityBreakdown,
          periodStates: [...periodStates],
          periodsAssigned: 0,
          periodsUnassigned: 0,
          errorMessage: msg,
        })
        throw new Error(msg)
      }

      const inserted: any[] = await insertResponse.json()

      // Build result assignments with joined data
      for (let i = 0; i < inserted.length; i++) {
        const rec = inserted[i]
        const periodCtx = periods.find((p) => p.period.id === rec.period_id)
        const substitute = rec.substitute_teacher_id
          ? teachers.find((t) => t.id === rec.substitute_teacher_id) ?? null
          : null

        resultAssignments.push({
          ...rec,
          class_section: periodCtx?.class_section ?? null,
          period: periodCtx?.period ?? null,
          subject: periodCtx?.subject ?? null,
          substitute_teacher: substitute,
          absent_teacher: { id: absence.teacher_id } as any,
        } as DraftAssignment)
      }
    }

    const periodsAssigned = resultAssignments.filter((a) => a.substitute_teacher_id !== null).length
    const periodsUnassigned = resultAssignments.filter(
      (a) => a.substitute_teacher_id === null
    ).length

    // Step 8: Write audit log entry
    await writeAuditEntry({
      school_id: absence.school_id,
      absence_id: absenceId,
      action: 'draft_generated',
      performed_by: null, // system action
      performed_by_name: 'System',
      target_date: targetDate,
      details: {
        periods_affected: periods.length,
        periods_assigned: periodsAssigned,
        periods_unassigned: periodsUnassigned,
      },
    })

    // Phase 4 — emit complete with real summary
    const elapsedMs = Date.now() - startTime

    onProgress?.({
      phase: 'complete',
      message: periodsAssigned === periods.length
        ? `All ${periods.length} periods covered automatically`
        : `${periodsAssigned} of ${periods.length} periods covered — ${periodsUnassigned} need attention`,
      absentTeacherName,
      periodsFound: periods.length,
      periodTimeRange,
      availability: availabilityBreakdown,
      periodStates: [...periodStates],
      periodsAssigned,
      periodsUnassigned,
      elapsedMs,
    })

    return {
      target_date: targetDate,
      absence_id: absenceId,
      periods_affected: periods.length,
      periods_assigned: periodsAssigned,
      periods_unassigned: periodsUnassigned,
      assignments: resultAssignments,
    }
  })
}

// ============================================================================
// triggerEngineForTomorrow
// ============================================================================

/**
 * Run the engine for all absences covering the next working day for a school.
 * Used by the manual "Prepare Now" button and the auto-generate cron job.
 *
 * Skips absences that:
 * - Are cancelled
 * - Already have a draft or posted plan for the target date
 * - Have an active lock on the target date
 *
 * @param onProgress Optional callback — called for each absence processed.
 *   The UI uses this to drive the bulk progress animation.
 */
export async function triggerEngineForTomorrow(
  schoolId: string,
  onProgress?: AutoAssignProgressCallback
): Promise<BulkEngineResult> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Determine the next working day
    const targetDate = await getNextWorkingDay(schoolId, accessToken)

    // Find all absences covering the target date
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', 'id,teacher_id,school_id,status,absence_type,start_time,end_time,date,start_date,end_date')
    absenceParams.append('school_id', `eq.${schoolId}`)
    absenceParams.append('start_date', `lte.${targetDate}`)
    absenceParams.append('end_date', `gte.${targetDate}`)
    absenceParams.append('status', `neq.cancelled`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch absences for tomorrow')
    }

    const absences: any[] = await absenceResponse.json()

    const results: EngineResult[] = []
    let totalAssignmentsCreated = 0

    // Shared across all absence runs — prevents double-booking the same teacher
    // at the same period when multiple absences overlap in time
    const sharedDraftBatchAssignedPeriods = new Map<string, Array<{ time_start: string; time_end: string }>>()

    for (const absence of absences) {
      // Check for active lock on this absence+date
      const lockParams = new URLSearchParams()
      lockParams.append('select', 'id,expires_at')
      lockParams.append('absence_id', `eq.${absence.id}`)
      lockParams.append('lock_date', `eq.${targetDate}`)
      lockParams.append('limit', '1')

      const lockResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/absence_day_locks?${lockParams}`,
        { headers }
      )

      if (lockResponse.ok) {
        const locks: any[] = await lockResponse.json()
        if (locks.length > 0) {
          const lock = locks[0]
          const isExpired = new Date(lock.expires_at) < new Date()
          if (!isExpired) {
            // Skip — locked by an admin
            results.push({
              target_date: targetDate,
              absence_id: absence.id,
              periods_affected: 0,
              periods_assigned: 0,
              periods_unassigned: 0,
              assignments: [],
              skipped_reason: 'draft_exists', // closest available reason
            })
            continue
          }
        }
      }

      // Run engine for this absence
      const result = await triggerEngine(absence.id, targetDate, onProgress, sharedDraftBatchAssignedPeriods)
      results.push(result)
      totalAssignmentsCreated += result.assignments.length
    }

    return {
      target_date: targetDate,
      absences_processed: absences.length,
      total_assignments_created: totalAssignmentsCreated,
      results,
    }
  })
}

// ============================================================================
// getNextWorkingDay
// ============================================================================

/**
 * Determine the next working day for a school.
 * Checks for holidays and alternate Saturday overrides.
 * Falls back to simple weekday calculation if no overrides found.
 */
async function getNextWorkingDay(schoolId: string, accessToken: string): Promise<string> {
  const headers = authHeaders(accessToken)
  let candidate = addDays(todayISO(), 1)

  // Try up to 14 days ahead to find a working day
  for (let i = 0; i < 14; i++) {
    // Check for holiday or alternate_saturday_off override on this candidate date
    // schedule_overrides uses start_date/end_date range columns
    const overrideParams = new URLSearchParams()
    overrideParams.append('select', 'override_type')
    overrideParams.append('school_id', `eq.${schoolId}`)
    overrideParams.append('start_date', `lte.${candidate}`)
    overrideParams.append('end_date', `gte.${candidate}`)
    overrideParams.append('override_type', `in.(holiday,alternate_saturday_off)`)

    const overrideResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/schedule_overrides?${overrideParams}`,
      { headers }
    )

    if (overrideResponse.ok) {
      const overrides: any[] = await overrideResponse.json()
      if (overrides.length > 0) {
        candidate = addDays(candidate, 1)
        continue
      }
    }

    // This is a working day
    return candidate
  }

  // Fallback: return tomorrow if no working day found in 14 days
  return addDays(todayISO(), 1)
}

// ============================================================================
// discardDraft
// ============================================================================

/**
 * Delete all draft assignments for a given absence+date pair.
 * Writes a `draft_discarded` audit log entry.
 * Requirements: 4.6, 5.6, 5.7
 */
export async function discardDraft(absenceId: string, date: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch absence to get school_id for audit log
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', 'school_id')
    absenceParams.append('id', `eq.${absenceId}`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    let schoolId = ''
    if (absenceResponse.ok) {
      const rows: any[] = await absenceResponse.json()
      schoolId = rows[0]?.school_id ?? ''
    }

    // Delete all draft assignments for this absence+date
    const deleteParams = new URLSearchParams()
    deleteParams.append('absence_id', `eq.${absenceId}`)
    deleteParams.append('date', `eq.${date}`)
    deleteParams.append('status', `eq.draft`)

    const deleteResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${deleteParams}`,
      {
        method: 'DELETE',
        headers,
      }
    )

    if (!deleteResponse.ok) {
      const err = await deleteResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to discard draft assignments')
    }

    // Write audit log entry
    await writeAuditEntry({
      school_id: schoolId,
      absence_id: absenceId,
      action: 'draft_discarded',
      target_date: date,
      details: { date },
    })
  })
}

// ============================================================================
// discardAllDrafts
// ============================================================================

/**
 * Delete ALL draft assignments for every absence on a given date for a school.
 * Used by the "Discard All" toolbar button on the substitutions page.
 *
 * Only deletes `draft` status assignments — posted assignments are never touched.
 * Writes one `draft_discarded` audit log entry per affected absence.
 *
 * Requirements: 4.6, 5.6, 5.7
 */
export async function discardAllDrafts(schoolId: string, date: string): Promise<{ discarded: number }> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Find all absences for this school that cover the target date
    // (we need absence IDs to scope the delete and write audit entries)
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', 'id')
    absenceParams.append('school_id', `eq.${schoolId}`)
    absenceParams.append('start_date', `lte.${date}`)
    absenceParams.append('end_date', `gte.${date}`)
    absenceParams.append('status', `neq.cancelled`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch absences for discard')
    }

    const absences: { id: string }[] = await absenceResponse.json()
    if (absences.length === 0) return { discarded: 0 }

    const absenceIds = absences.map((a) => a.id)

    // Delete all draft assignments for these absences on this date in one batch
    const deleteParams = new URLSearchParams()
    deleteParams.append('absence_id', `in.(${absenceIds.join(',')})`)
    deleteParams.append('date', `eq.${date}`)
    deleteParams.append('status', `eq.draft`)

    const deleteResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${deleteParams}`,
      {
        method: 'DELETE',
        headers: {
          ...headers,
          Prefer: 'return=representation',
        },
      }
    )

    if (!deleteResponse.ok) {
      const err = await deleteResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to discard all draft assignments')
    }

    const deleted: any[] = await deleteResponse.json()
    const discarded = deleted.length

    // Write one audit entry per absence that had drafts deleted
    const affectedAbsenceIds = [...new Set(deleted.map((d) => d.absence_id as string))]
    await Promise.all(
      affectedAbsenceIds.map((absenceId) =>
        writeAuditEntry({
          school_id: schoolId,
          absence_id: absenceId,
          action: 'draft_discarded',
          target_date: date,
          details: { date, bulk: true },
        }).catch(() => {
          // Audit log failure must not block the discard
        })
      )
    )

    return { discarded }
  })
}

// ============================================================================
// rerunAutoAssignment
// ============================================================================

/**
 * Re-run the engine only for periods whose existing assignment has
 * `assignment_method = 'automatic'`. Manual assignments are preserved.
 * Requirements: 4.6, 5.6, 5.7
 */
export async function rerunAutoAssignment(
  absenceId: string,
  date: string
): Promise<EngineResult> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch all current draft assignments for this absence+date
    const assignParams = new URLSearchParams()
    assignParams.append('select', '*')
    assignParams.append('absence_id', `eq.${absenceId}`)
    assignParams.append('date', `eq.${date}`)
    assignParams.append('status', `eq.draft`)

    const assignResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${assignParams}`, {
      headers,
    })

    if (!assignResponse.ok) {
      const err = await assignResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch existing assignments')
    }

    const existingAssignments: any[] = await assignResponse.json()

    // Separate manual and automatic assignments
    const manualAssignments = existingAssignments.filter(
      (a) => a.assignment_method === 'manual'
    )
    const automaticAssignments = existingAssignments.filter(
      (a) => a.assignment_method === 'automatic'
    )

    // Delete only the automatic assignments — manual ones stay
    if (automaticAssignments.length > 0) {
      const autoIds = automaticAssignments.map((a) => a.id).join(',')
      const deleteResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/assignments?id=in.(${autoIds})`,
        {
          method: 'DELETE',
          headers,
        }
      )

      if (!deleteResponse.ok) {
        const err = await deleteResponse.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to delete automatic assignments for re-run')
      }
    }

    // Fetch absence to get school_id and absence details
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', '*')
    absenceParams.append('id', `eq.${absenceId}`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch absence')
    }

    const absences: any[] = await absenceResponse.json()
    if (!absences || absences.length === 0) throw new Error('Absence not found')
    const absence = absences[0]

    // Load rules
    const rules: SubstitutionRules = await getSubstitutionRules(absence.school_id)

    // Discover periods
    const discoveryResult = await discoverPeriods(
      absence.teacher_id,
      date,
      absence.absence_type,
      absence.start_time,
      absence.end_time,
      absence.school_id
    )

    if (discoveryResult.skipped_reason) {
      return {
        target_date: date,
        absence_id: absenceId,
        periods_affected: 0,
        periods_assigned: 0,
        periods_unassigned: 0,
        assignments: [],
        skipped_reason: discoveryResult.skipped_reason,
      }
    }

    // Filter out periods already covered by manual assignments
    const manualPeriodIds = new Set(manualAssignments.map((a) => a.period_id))
    const periodsToProcess = discoveryResult.periods.filter(
      (p) => !manualPeriodIds.has(p.period.id)
    )

    if (periodsToProcess.length === 0) {
      return {
        target_date: date,
        absence_id: absenceId,
        periods_affected: manualAssignments.length,
        periods_assigned: manualAssignments.filter((a) => a.substitute_teacher_id !== null).length,
        periods_unassigned: manualAssignments.filter((a) => a.substitute_teacher_id === null).length,
        assignments: [],
      }
    }

    // Fetch teachers and run engine for the non-manual periods
    const { teachers, teacherSubjects, teacherClassAssociations, teacherClassGroupAssociations } =
      await fetchTeachersWithRelations(absence.school_id, accessToken)

    const postedAssignmentsByTeacher = await fetchPostedAssignmentCounts(date, accessToken)
    const dayOfWeek = getDayOfWeek(date)
    const draftBatchCounts = new Map<string, { daily: number; weekly: number }>()
    const currentRunAssignedTeacherIds = new Set<string>()
    const draftBatchAssignedPeriods = new Map<string, Array<{ time_start: string; time_end: string }>>()

    const assignmentsToInsert: any[] = []
    const resultAssignments: DraftAssignment[] = []

    for (const periodCtx of periodsToProcess) {
      const eligibilityResult = await filterEligibleCandidates(teachers, {
        targetDate: date,
        periodId: periodCtx.period.id,
        periodTimeStart: periodCtx.period.time_start,
        periodTimeEnd: periodCtx.period.time_end,
        dayOfWeek,
        schoolId: absence.school_id,
        absentTeacherId: absence.teacher_id,
        rules,
        draftBatchCounts,
        draftBatchAssignedPeriods,
      })

      const eligibleCandidates = eligibilityResult.candidates

      let substituteTeacherId: string | null = null
      let unassignedReason: string | null = null

      if (eligibleCandidates.length === 0) {
        unassignedReason = 'no_eligible_teachers'
      } else {
        const { sorted } = sortCandidates(eligibleCandidates, {
          period: periodCtx,
          rules,
          postedAssignmentsByTeacher,
          currentRunAssignedTeacherIds,
          teacherSubjects,
          teacherClassAssociations,
          teacherClassGroupAssociations,
        })

        if (rules.assignment_mode === 'auto') {
          const topCandidate = sorted[0]
          substituteTeacherId = topCandidate.id

          if (!draftBatchCounts.has(topCandidate.id)) {
            draftBatchCounts.set(topCandidate.id, { daily: 0, weekly: 0 })
          }
          const counts = draftBatchCounts.get(topCandidate.id)!
          counts.daily++
          counts.weekly++
          currentRunAssignedTeacherIds.add(topCandidate.id)

          // Record this time slot as taken by this teacher
          if (!draftBatchAssignedPeriods.has(topCandidate.id)) {
            draftBatchAssignedPeriods.set(topCandidate.id, [])
          }
          draftBatchAssignedPeriods.get(topCandidate.id)!.push({
            time_start: periodCtx.period.time_start,
            time_end: periodCtx.period.time_end,
          })
        }
      }

      assignmentsToInsert.push({
        school_id: absence.school_id,
        absence_id: absenceId,
        date,
        class_section_id: periodCtx.class_section_id,
        period_id: periodCtx.period.id,
        subject_id: periodCtx.subject_id,
        absent_teacher_id: absence.teacher_id,
        substitute_teacher_id: substituteTeacherId,
        status: 'draft',
        assignment_method: 'automatic',
        unassigned_reason: unassignedReason,
      })
    }

    if (assignmentsToInsert.length > 0) {
      const insertResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments`, {
        method: 'POST',
        headers: {
          ...headers,
          Prefer: 'return=representation,resolution=ignore-duplicates',
        },
        body: JSON.stringify(assignmentsToInsert),
      })

      if (!insertResponse.ok) {
        const err = await insertResponse.json().catch(() => ({}))
        throw new Error(err.message || 'Failed to insert re-run assignments')
      }

      const inserted: any[] = await insertResponse.json()
      for (const rec of inserted) {
        const periodCtx = periodsToProcess.find((p) => p.period.id === rec.period_id)
        const substitute = rec.substitute_teacher_id
          ? teachers.find((t) => t.id === rec.substitute_teacher_id) ?? null
          : null

        resultAssignments.push({
          ...rec,
          class_section: periodCtx?.class_section ?? null,
          period: periodCtx?.period ?? null,
          subject: periodCtx?.subject ?? null,
          substitute_teacher: substitute,
          absent_teacher: { id: absence.teacher_id } as any,
        } as DraftAssignment)
      }
    }

    const allAssigned = [
      ...resultAssignments,
      ...manualAssignments,
    ]

    const periodsAssigned = allAssigned.filter((a) => a.substitute_teacher_id !== null).length
    const periodsUnassigned = allAssigned.filter((a) => a.substitute_teacher_id === null).length

    return {
      target_date: date,
      absence_id: absenceId,
      periods_affected: allAssigned.length,
      periods_assigned: periodsAssigned,
      periods_unassigned: periodsUnassigned,
      assignments: resultAssignments,
    }
  })
}
